import { useTranslation } from 'react-i18next';
import { Typography, Card, CardContent, Box, IconButton } from '@mui/material';
import { useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import DownloadReportDialog from './components/DownloadReportDialog';
import { ReportType } from '@/api/enum/reportType.enum';

// Filters each report supports. Used to build the download dialog.
export type ReportFilter = 'start' | 'end' | 'series' | 'hasOnline';

// Meaning of the start/end date range, decides the date picker labels.
//   register - 註冊時間 (when the user registered)
//   period   - 時段 (the record's time period)
export type ReportDateScope = 'register' | 'period';

export interface ReportListItem {
	id: ReportType;
	nameKey: string;
	filters: ReportFilter[];
	dateScope?: ReportDateScope;
}

// Hardcoded report definitions.
export const REPORT_LIST: ReportListItem[] = [
	{ id: ReportType.UserBasicData, nameKey: 'reportList.userBasicData', filters: ['start', 'end', 'hasOnline'], dateScope: 'register' },
	{ id: ReportType.PracticeData, nameKey: 'reportList.practiceData', filters: [] },
	{ id: ReportType.TaskData, nameKey: 'reportList.taskData', filters: [] },
	{ id: ReportType.UserLoginRecord, nameKey: 'reportList.userLoginRecord', filters: ['start', 'end'], dateScope: 'period' },
	{ id: ReportType.UserCompletePracticeRecord, nameKey: 'reportList.userCompletePracticeRecord', filters: ['start', 'end'], dateScope: 'period' },
	{ id: ReportType.Day21SurveyData, nameKey: 'reportList.day21SurveyData', filters: ['start', 'end'], dateScope: 'period' },
	{ id: ReportType.HealthIndexRecord, nameKey: 'reportList.healthIndexRecord', filters: ['start', 'end'], dateScope: 'period' },
	{ id: ReportType.UserCollectionData, nameKey: 'reportList.userCollectionData', filters: [] },
	{ id: ReportType.UserDrawBoxRecord, nameKey: 'reportList.userDrawBoxRecord', filters: ['start', 'end'], dateScope: 'period' },
	{ id: ReportType.UserCompletePracticeTimerRecord, nameKey: 'reportList.userCompletePracticeTimerRecord', filters: ['start', 'end'], dateScope: 'period' },
	{ id: ReportType.TreasureData, nameKey: 'reportList.treasureData', filters: [] },
	{ id: ReportType.UserObtainTreasureRecord, nameKey: 'reportList.userObtainTreasureRecord', filters: ['start', 'end'], dateScope: 'period' },
];

export default function ReportListSummaryPage() {
	const { t } = useTranslation();
	const [selectedReport, setSelectedReport] = useState<ReportListItem | null>(
		null
	);
	const [open, setOpen] = useState(false);

	const handleDownload = (report: ReportListItem) => {
		setSelectedReport(report);
		setOpen(true);
	};

	const rows = REPORT_LIST.map((report) => ({
		...report,
		name: t(report.nameKey),
	}));

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: t('reportList.reportName'),
			flex: 1,
		},
		{
			field: 'download',
			headerName: '',
			width: 68,
			sortable: false,
			renderCell: (params) => (
				<IconButton
					color='primary'
					onClick={() => handleDownload(params.row)}
				>
					<FileDownloadOutlinedIcon />
				</IconButton>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.reportList')}</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<Box marginTop={2}>
						<LocalDataGrid
							rows={rows}
							columns={columns}
							rowCount={0}
							getRowId={(row) => row.id}
							pagination={false}
						/>
					</Box>
				</CardContent>
			</Card>

			<DownloadReportDialog
				report={selectedReport}
				open={open}
				onClose={() => setOpen(false)}
			/>
		</>
	);
}
